import React, { useCallback, useMemo, useState } from "react";
import { DatePicker, Input, Modal, Radio, Select, Tag, Tooltip } from "antd";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import moment from "moment";
import Events from "components/events";
import Icons from "components/icons";
import { Tables } from "components/layout";
import { Localize } from "components/service";
import { useBreadCrumbs } from "components/use-hooks";
import checkInLogsMock from "components/events/mock-data/check-in-logs";
import attendanceShiftsMock from "components/events/mock-data/attendance-shifts";
import { timeFormat } from "utils/helper-functions";
import {
    attachScanLogsToShifts,
    buildAttendanceDailySummaries,
    formatWorkedSeconds,
    getLocationLatitude,
    getLocationLongitude,
    hasLocationCoordinates,
    hasScanLocation
} from "components/events/helpers/event-attendance-helper";

const { RangePicker } = DatePicker;
const { Search } = Input;
const { Option } = Select;
const basePath = Events.Const.basePath;
const DAILY_SUMMARY_TABLE = "daily_summary";
const ATTENDANCE_SHIFTS_TABLE = "attendance_shifts";
const SCAN_LOGS_TABLE = "scan_logs";

const attendanceShiftsWithScanLogs = attachScanLogsToShifts(attendanceShiftsMock, checkInLogsMock);
const attendanceDailySummaries = buildAttendanceDailySummaries(attendanceShiftsWithScanLogs);

const tableSources = {
    [ DAILY_SUMMARY_TABLE ]: {
        mockData: attendanceDailySummaries,
        getDate: (item) => item.work_date,
        note: "CHECK_LOGS.Note_DailySummary",
        tableHelperFactory: Events.Helpers.TableAttendanceDailySummary,
    },
    [ ATTENDANCE_SHIFTS_TABLE ]: {
        mockData: attendanceShiftsWithScanLogs,
        getDate: (item) => item.check_in_at,
        note: "CHECK_LOGS.Note_AttendanceShifts",
        tableHelperFactory: Events.Helpers.TableAttendanceShifts,
    },
    [ SCAN_LOGS_TABLE ]: {
        mockData: checkInLogsMock,
        getDate: (item) => item.created_at,
        note: "CHECK_LOGS.Note_ScanLogs",
        tableHelperFactory: Events.Helpers.TableCheckInLogs,
    },
};

const getLocationLabel = (location) => {
    if (location?.isDailySummary)
    {
        return "CHECK_LOGS.Text_DailyScanLogs";
    }

    if (location?.isShiftDetails)
    {
        return "CHECK_LOGS.Text_ShiftScanLogs";
    }

    if (location?.tableSource === SCAN_LOGS_TABLE)
    {
        return "CHECK_LOGS.Text_ScanLocation";
    }

    return "CHECK_LOGS.Text_ScanLocation";
};

const getLocationModalTitle = (location) => (
    location?.isDailySummary ?
        "CHECK_LOGS.Modal_Title_DailyLogs" :
        location?.isShiftDetails ?
        "CHECK_LOGS.Modal_Title_ShiftScanLogs" :
        location?.tableSource === SCAN_LOGS_TABLE ?
        "CHECK_LOGS.Modal_Title_ScanLocation" :
        "CHECK_LOGS.Modal_Title_Location"
);

const getScanActionLabel = (action) => (
    action === "check_out" ?
        "CHECK_LOGS.Tag_CheckOut" :
        "CHECK_LOGS.Tag_CheckIn"
);

const getScanActionTooltip = (action) => (
    action === "check_out" ?
        "TABLES.Tooltip_CheckOutLocation" :
        "TABLES.Tooltip_CheckInLocation"
);

const EventCheckInOutSubcontractorsSubPage = ({ event }) => {
    const [ activeTableKey, setActiveTableKey ] = useState(DAILY_SUMMARY_TABLE);
    const [ dateRange, setDateRange ] = useState([]);
    const [ subcontractor, setSubcontractor ] = useState();
    const [ searchText, setSearchText ] = useState("");
    const [ selectedLocation, setSelectedLocation ] = useState();

    const breadcrumbs = [
        {
            label: Localize({ children: "BREADCRUMBS.Label_Exhibitions" }).props.children,
            path: `${ basePath }`
        },
        {
            label: event?.title,
            path: `${ basePath }/${ event?.id }`
        },
        {
            label: Localize({ children: "BREADCRUMBS.Label_CheckInOutSubcontractors" }).props.children,
            path: `${ basePath }/${ event?.id }/check-in-out-subcontractors`
        }
    ];

    useBreadCrumbs(breadcrumbs);

    const openLocation = useCallback((log) => {
        setSelectedLocation({
            ...log,
            tableSource: activeTableKey,
        });
    }, [ activeTableKey ]);
    const closeLocation = useCallback(() => setSelectedLocation(undefined), []);
    const activeTableSource = tableSources[ activeTableKey ];
    const tableHelperFactory = activeTableSource.tableHelperFactory;
    const tableHelper = useMemo(() => tableHelperFactory(openLocation), [ tableHelperFactory, openLocation ]);

    const handleTableChange = useCallback((key) => {
        setActiveTableKey(key);
        setSelectedLocation(undefined);
    }, []);

    const subcontractorOptions = useMemo(() => (
        [ ...new Set(activeTableSource.mockData.map(item => item.subcontractor.title)) ]
    ), [ activeTableSource ]);

    const filteredLogs = useMemo(() => {
        const normalizedSearch = searchText.trim().toLowerCase();

        // TODO: Replace mock sources with `attendanceDailySummaries(ee_id)`, `attendanceShifts(ee_id)` and `attendanceScanLogs(ee_id)` when backend exposes them.
        const filteredItems = activeTableSource.mockData.filter((item) => {
            const workerName = `${ item.worker.name } ${ item.worker.surname }`.toLowerCase();
            const createdAt = moment(activeTableSource.getDate(item));

            if (dateRange?.length === 2)
            {
                const start = dateRange[ 0 ].clone().startOf("day");
                const end = dateRange[ 1 ].clone().endOf("day");

                if (!createdAt.isBetween(start, end, undefined, "[]"))
                {
                    return false;
                }
            }

            if (subcontractor && item.subcontractor.title !== subcontractor)
            {
                return false;
            }

            if (normalizedSearch && !workerName.includes(normalizedSearch))
            {
                return false;
            }

            return true;
        });

        if (activeTableKey !== SCAN_LOGS_TABLE)
        {
            return filteredItems;
        }

        return [ ...filteredItems ].sort((firstItem, secondItem) => (
            moment(activeTableSource.getDate(secondItem)).valueOf() - moment(activeTableSource.getDate(firstItem)).valueOf()
        ));
    }, [ activeTableKey, activeTableSource, dateRange, subcontractor, searchText ]);

    const renderViewSwitcher = () => (
        <div className="event-check-view-switcher">
            <Radio.Group
                className="event-check-view-switcher__control"
                buttonStyle="solid"
                value={ activeTableKey }
                onChange={ (radioEvent) => handleTableChange(radioEvent.target.value) }>
                <Radio.Button value={ DAILY_SUMMARY_TABLE }>
                    <Localize>CHECK_LOGS.Tab_DailySummary</Localize>
                </Radio.Button>

                <Radio.Button value={ ATTENDANCE_SHIFTS_TABLE }>
                    <Localize>CHECK_LOGS.Tab_AttendanceShifts</Localize>
                </Radio.Button>

                <Radio.Button value={ SCAN_LOGS_TABLE }>
                    <Localize>CHECK_LOGS.Tab_ScanLogs</Localize>
                </Radio.Button>
            </Radio.Group>

            <div className={
                activeTableKey === SCAN_LOGS_TABLE ?
                    "event-check-view-switcher__note event-check-view-switcher__note--concept" :
                    "event-check-view-switcher__note"
            }>
                <InfoCircleOutlined />
                <span>
                    <Localize>{ activeTableSource.note }</Localize>
                </span>
            </div>
        </div>
    );

    const renderFilters = () => (
        <div className="table-action-bar event-check-logs-filter-bar">
            <div className="col event-check-logs-filter-bar__filters">
                <Search
                    className="filter-search"
                    prefix={ <SearchOutlined /> }
                    placeholder={ Localize({ children: "SEARCH.Input_Placeholder_CheckWorker" }).props.children }
                    value={ searchText }
                    onChange={ (event) => setSearchText(event.target.value) } />

                <RangePicker
                    separator="&ndash;"
                    value={ dateRange?.length === 2 ? dateRange : undefined }
                    placeholder={ [
                        Localize({ children: "FORMS.Input_Placeholder_StartDate" }).props.children,
                        Localize({ children: "FORMS.Input_Placeholder_EndDate" }).props.children
                    ] }
                    onChange={ (value) => setDateRange(value || []) } />

                <Select
                    allowClear
                    className="event-check-logs-filter-bar__select"
                    placeholder={ Localize({ children: "FORMS.Input_Label_Subcontractor" }).props.children }
                    suffixIcon={ <Icons.Arrow /> }
                    value={ subcontractor }
                    onChange={ setSubcontractor }>
                    { subcontractorOptions.map((title) => (
                        <Option key={ title } value={ title }>{ title }</Option>
                    )) }
                </Select>
            </div>
        </div>
    );

    const selectedLocationLatitude = getLocationLatitude(selectedLocation);
    const selectedLocationLongitude = getLocationLongitude(selectedLocation);
    const selectedLocationHistory = selectedLocation?.scan_logs || selectedLocation?.points || [];
    const selectedLocationPoints = selectedLocation?.points || selectedLocationHistory.filter(hasScanLocation);
    const selectedLocationHasCoordinates = selectedLocation ? hasLocationCoordinates(selectedLocation) : false;
    const selectedLocationHasMapData = selectedLocationHasCoordinates || selectedLocationPoints.length > 0;

    return (
        <>
            { renderViewSwitcher() }

            <Tables.Mock
                mockData={ filteredLogs }
                tableHelper={ tableHelper }
                tableLayout="auto">
                { renderFilters() }
            </Tables.Mock>

            <Modal
                className="modal-standard event-check-location-modal"
                centered
                visible={ !!selectedLocation }
                footer={ null }
                width={ 520 }
                destroyOnClose={ true }
                onCancel={ closeLocation }>
                <strong className="modal-title">
                    <Localize>{ getLocationModalTitle(selectedLocation) }</Localize>
                </strong>

                { selectedLocation &&
                    <>
                        <div className="event-check-location-modal__meta">
                            <span>
                                { selectedLocation.worker.name } { selectedLocation.worker.surname }
                            </span>
                            <span>
                                { selectedLocation.isDailySummary ?
                                    timeFormat(selectedLocation.work_date, "DD.MM.YYYY") :
                                    selectedLocation.isShiftDetails ?
                                    <Localize>CHECK_LOGS.Text_ShiftCycle</Localize> :
                                    <Localize>{ getLocationLabel(selectedLocation) }</Localize>
                                }
                            </span>
                            <span>
                                { selectedLocation.isDailySummary ?
                                    <>
                                        <Localize>CHECK_LOGS.Text_Worked</Localize>: { formatWorkedSeconds(selectedLocation.worked_seconds) }
                                    </> :
                                    selectedLocation.isShiftDetails ?
                                    <>
                                        { timeFormat(selectedLocation.check_in_at, "DD.MM.YYYY HH:mm:ss") } - { selectedLocation.check_out_at ? timeFormat(selectedLocation.check_out_at, "HH:mm:ss") : "-" }
                                    </> :
                                    selectedLocationHasCoordinates ?
                                    `${ selectedLocationLatitude }, ${ selectedLocationLongitude }`
                                    :
                                    <Localize>CHECK_LOGS.Text_NoGpsPoint</Localize>
                                }
                            </span>
                        </div>

                        { selectedLocation.accuracy !== null && selectedLocation.accuracy !== undefined &&
                            <Tooltip title={ <Localize>CHECK_LOGS.Tooltip_Accuracy</Localize> }>
                                <div className="event-check-location-modal__accuracy">
                                    <Localize>CHECK_LOGS.Text_Accuracy</Localize>: { selectedLocation.accuracy } m
                                </div>
                            </Tooltip>
                        }

                        { selectedLocation.isDailySummary &&
                            <div className="event-check-location-modal__accuracy">
                                <Localize>CHECK_LOGS.Text_DailyScanLogsDescription</Localize>
                            </div>
                        }

                        { selectedLocation.isShiftDetails &&
                            <div className="event-check-location-modal__accuracy">
                                <Localize>CHECK_LOGS.Text_ShiftScanLogsDescription</Localize>
                            </div>
                        }

                        { selectedLocationHistory.length > 0 &&
                            <div className="event-check-location-history">
                                <Tooltip title={ <Localize>CHECK_LOGS.Tooltip_ScanLogHistory</Localize> }>
                                    <strong>
                                        <Localize>CHECK_LOGS.Text_ScanLogHistory</Localize>
                                        { ` (${ selectedLocationHistory.length })` }
                                    </strong>
                                </Tooltip>

                                { selectedLocationHistory.map((log) => (
                                    <div key={ log.id } className="event-check-location-history__item">
                                        <Tooltip title={ <Localize>{ getScanActionTooltip(log.action) }</Localize> }>
                                            <span className="event-check-location-history__source">
                                                <Tag color={ log.action === "check_out" ? "blue" : "magenta" }>
                                                    <Localize>{ getScanActionLabel(log.action) }</Localize>
                                                </Tag>
                                                <InfoCircleOutlined />
                                            </span>
                                        </Tooltip>
                                        <span>{ moment(log.created_at).format("DD.MM.YYYY HH:mm:ss") }</span>
                                        <span>
                                            { log.latitude !== null && log.latitude !== undefined && log.longitude !== null && log.longitude !== undefined ?
                                                `${ log.latitude }, ${ log.longitude }` :
                                                "-"
                                            }
                                        </span>
                                        <Tooltip title={ <Localize>CHECK_LOGS.Tooltip_Accuracy</Localize> }>
                                            <span>
                                                { log.accuracy !== null && log.accuracy !== undefined ? `${ log.accuracy } m` : "-" }
                                            </span>
                                        </Tooltip>
                                    </div>
                                )) }
                            </div>
                        }

                        { selectedLocationHasMapData ?
                            <Events.Fields.LocationPreviewMap
                                latitude={ selectedLocationLatitude }
                                longitude={ selectedLocationLongitude }
                                radius={ event?.check_radius }
                                points={ selectedLocationPoints } /> :
                            <div className="event-check-location-modal__empty">
                                <Localize>CHECK_LOGS.Text_NoGpsPoint</Localize>
                            </div>
                        }
                    </>
                }
            </Modal>
        </>
    );
};

export default EventCheckInOutSubcontractorsSubPage;
